(function () {
  'use strict';

  // A timed-out write may still reach Shopify. Never retry it automatically,
  // or let reopening the picker submit it again without reviewing the cart.
  async function add(component, { items, sampleIds, maxItems, skipCart = true } = {}) {
    if (component.isLoading || component.cartUncertain || component.cartSubmitComplete) return;
    component.isLoading = true;
    component.cartError = null;
    component.itemsAddedToCart = false;
    let writeStarted = false;
    const controller = new AbortController();
    let timer;
    const deadline = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Sample cart request timed out.'));
      }, 15000);
    });
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const request = async (path, options = {}) => {
      const response = await Promise.race([
        fetch(root + path, { ...options, signal: controller.signal }), deadline,
      ]);
      if (!response.ok) throw new Error('Sample cart request failed.');
      return Promise.race([response.json(), deadline]);
    };

    try {
      const allowed = new Set((sampleIds || []).map(String));
      const unique = new Map();
      for (const item of items || []) {
        if (!allowed.has(String(item.id)) || item.quantity !== 1) throw new Error('Invalid sample selection.');
        unique.set(String(item.id), item);
      }
      if (!unique.size || !Number.isInteger(maxItems) || maxItems < 1 || unique.size > maxItems) {
        throw new Error('Invalid sample selection.');
      }

      // Preserve every existing line. A normal repeat request only adds samples
      // that are absent, and the limit includes samples already in this cart.
      const cart = await request('cart.js');
      if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response.');
      const present = new Set();
      let sampleCount = 0;
      for (const item of cart.items) {
        const id = String(item.variant_id ?? item.id);
        if (!allowed.has(id)) continue;
        if (!Number.isInteger(item.quantity) || item.quantity < 0) throw new Error('Invalid cart quantity.');
        if (item.quantity > 0) present.add(id);
        sampleCount += item.quantity;
      }
      const missing = [...unique].filter(([id]) => !present.has(id)).map(([, item]) => item);
      if (sampleCount + missing.length > maxItems) {
        component.cartError = 'limit';
        return;
      }
      if (missing.length) {
        writeStarted = true;
        const result = await request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: missing }),
        });
        if (!Array.isArray(result?.items) || !missing.every(item => result.items.some(added =>
          String(added.variant_id ?? added.id) === String(item.id) && added.quantity >= 1,
        ))) throw new Error('Sample addition could not be confirmed.');
      }
      component.itemsAddedToCart = true;
      component.cartSubmitComplete = skipCart;
      if (skipCart) window.location.href = root + 'checkout';
    } catch (error) {
      component.cartUncertain = writeStarted;
      component.cartError = writeStarted ? 'review' : 'retry';
    } finally {
      clearTimeout(timer);
      controller.abort();
      component.isLoading = false;
    }
  }

  window.MaisonSampleCart = Object.freeze({ add });
})();
